import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { usePanelTokens } from './tokens';

export interface RingGaugeProps {
  /** 0–100. */
  value: number;
  size?: number;
  stroke?: number;
  color?: string;
  trackColor?: string;
  centerTop?: ReactNode;
  centerBottom?: ReactNode;
}

/** A single-value donut ring with optional center label (pure SVG, crisp). */
export function RingGauge({
  value,
  size = 112,
  stroke = 11,
  color,
  trackColor,
  centerTop,
  centerBottom,
}: RingGaugeProps) {
  const t = usePanelTokens();
  const arc = color ?? t.a;
  const track = trackColor ?? t.aSoft;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value));
  const offset = c * (1 - pct / 100);
  return (
    <Box sx={{ position: 'relative', width: size, height: size, flex: 'none' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={arc}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
      </svg>
      {(centerTop != null || centerBottom != null) && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {centerTop != null && (
            <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: t.ink, lineHeight: 1 }}>
              {centerTop}
            </Typography>
          )}
          {centerBottom != null && (
            <Typography sx={{ fontSize: '0.625rem', color: t.label, mt: 0.4 }}>
              {centerBottom}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

export interface ConcentricRingsProps {
  /** Outer → inner. Each: value 0–100 + color. */
  rings: { value: number; color: string }[];
  size?: number;
  stroke?: number;
  gap?: number;
}

/** Nested tonal rings — the "resource usage" widget. */
export function ConcentricRings({ rings, size = 156, stroke = 9, gap = 5 }: ConcentricRingsProps) {
  const t = usePanelTokens();
  const cx = size / 2;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flex: 'none' }}>
      {rings.map((ring, i) => {
        const r = (size - stroke) / 2 - i * (stroke + gap);
        if (r <= 0) return null;
        const c = 2 * Math.PI * r;
        const pct = Math.max(0, Math.min(100, ring.value));
        return (
          <g key={i}>
            <circle cx={cx} cy={cx} r={r} fill="none" stroke={t.aSoft} strokeWidth={stroke} />
            <circle
              cx={cx}
              cy={cx}
              r={r}
              fill="none"
              stroke={ring.color}
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={c}
              strokeDashoffset={c * (1 - pct / 100)}
              transform={`rotate(-90 ${cx} ${cx})`}
              style={{ transition: 'stroke-dashoffset 0.6s ease' }}
            />
          </g>
        );
      })}
    </svg>
  );
}

/** Small colored legend dot + label row. */
export function LegendDot({ color, children }: { color: string; children: ReactNode }) {
  const t = usePanelTokens();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.78rem', color: t.label }}>
      <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: color, flex: 'none' }} />
      {children}
    </Box>
  );
}
